import React, { useCallback, useEffect, useState } from 'react';
import { bookmarksAPI } from '../api/client';

/**
 * A ⭐/☆ toggle for pinning a document as pertinent (GitHub issue #59).
 * Mounted on DocumentViewPage.js beside "🔗 Copy Permalink".
 *
 * State always comes from the server, never held only locally -- same rule
 * ImpersonationBanner follows for the same reason: a locally-set flag is lost
 * on refresh and would silently disagree with what's actually bookmarked.
 * There is no per-document "is this bookmarked" endpoint, so this fetches the
 * caller's whole bookmark list and matches on documentId -- the same
 * self-contained fetch-and-filter pattern ApprovalActionBar.js uses for its
 * own pending-approval row, and just as cheap: a personal bookmark list is
 * never going to be a 500-row page.
 *
 * Folder assignment on first bookmark is a simple <select> here (create /
 * rename / delete folders happens on the Profile page's BookmarksPanel,
 * which needs the fuller UI); moving an existing bookmark between folders
 * later is also done from there, not from this button.
 */
function BookmarkButton({ documentId }) {
  const [bookmark, setBookmark] = useState(null);
  const [folders, setFolders] = useState([]);
  const [choosingFolder, setChoosingFolder] = useState(false);
  const [folderId, setFolderId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await bookmarksAPI.list();
      const match = res.data.find((b) => b.document_id === Number(documentId));
      setBookmark(match || null);
    } catch (err) {
      // Non-fatal and deliberately silent, same reasoning ApprovalActionBar
      // gives for its own fetch -- this must never stop the document page
      // from rendering.
      console.error('Error loading bookmarks:', err);
    }
  }, [documentId]);

  useEffect(() => { refresh(); }, [refresh]);

  const startAdding = async () => {
    setError(null);
    try {
      const res = await bookmarksAPI.listFolders();
      setFolders(res.data);
    } catch (err) {
      setFolders([]);
    }
    setFolderId('');
    setChoosingFolder(true);
  };

  const confirmAdd = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await bookmarksAPI.create(Number(documentId), folderId ? Number(folderId) : null);
      setBookmark(res.data);
      setChoosingFolder(false);
    } catch (err) {
      setError('Could not bookmark this document.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!bookmark) return;
    setBusy(true);
    setError(null);
    try {
      await bookmarksAPI.remove(bookmark.id);
      setBookmark(null);
    } catch (err) {
      setError('Could not remove the bookmark.');
    } finally {
      setBusy(false);
    }
  };

  if (choosingFolder) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <select value={folderId} onChange={(e) => setFolderId(e.target.value)}>
          <option value="">Uncategorized</option>
          {folders.map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
        <button type="button" className="btn btn-primary btn-small" onClick={confirmAdd} disabled={busy}>
          ⭐ Save
        </button>
        <button type="button" className="btn btn-secondary btn-small"
                onClick={() => setChoosingFolder(false)} disabled={busy}>
          Cancel
        </button>
      </span>
    );
  }

  return (
    <span>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={bookmark ? remove : startAdding}
        disabled={busy}
        title={bookmark ? 'Remove bookmark' : 'Bookmark this policy'}
      >
        {bookmark ? '⭐ Bookmarked' : '☆ Bookmark'}
      </button>
      {error && <span style={{ color: 'var(--danger-ink)', fontSize: '12px', marginLeft: '8px' }}>{error}</span>}
    </span>
  );
}

export default BookmarkButton;
